// Editor de anotaciones sobre la captura (canvas nativo, sin dependencias).
// Las anotaciones son vectoriales (coordenadas de la imagen original) para
// poder deshacer y para aplanarlas a resolucion completa al enviar.
import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState,
} from "react";

const HERRAMIENTAS = [
  { key: "rect", label: "Rectángulo", icon: "fa-regular fa-square" },
  { key: "arrow", label: "Flecha", icon: "fa-solid fa-arrow-right-long" },
  { key: "text", label: "Texto", icon: "fa-solid fa-font" },
  { key: "pen", label: "Lápiz libre", icon: "fa-solid fa-pencil" },
];

const COLORES = [
  { key: "#ef4444", label: "Rojo" },
  { key: "#facc15", label: "Amarillo" },
  { key: "#3b82f6", label: "Azul" },
];

const grosor = (w) => Math.max(3, Math.round(w / 360));
const tamTexto = (w) => Math.max(20, Math.round(w / 55));

function dibujarFlecha(ctx, { x1, y1, x2, y2 }, lw) {
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const cab = lw * 5;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - cab * Math.cos(ang - Math.PI / 6), y2 - cab * Math.sin(ang - Math.PI / 6));
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - cab * Math.cos(ang + Math.PI / 6), y2 - cab * Math.sin(ang + Math.PI / 6));
  ctx.stroke();
}

function dibujarFormas(ctx, formas, anchoImg) {
  const lw = grosor(anchoImg);
  const fs = tamTexto(anchoImg);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = lw;
  formas.forEach((f) => {
    ctx.strokeStyle = f.color;
    ctx.fillStyle = f.color;
    if (f.tipo === "rect") {
      ctx.strokeRect(Math.min(f.x1, f.x2), Math.min(f.y1, f.y2), Math.abs(f.x2 - f.x1), Math.abs(f.y2 - f.y1));
    } else if (f.tipo === "arrow") {
      dibujarFlecha(ctx, f, lw);
    } else if (f.tipo === "pen") {
      ctx.beginPath();
      f.pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      if (f.pts.length === 1) ctx.lineTo(f.pts[0].x + 0.1, f.pts[0].y);
      ctx.stroke();
    } else if (f.tipo === "text") {
      ctx.font = `700 ${fs}px Inter, system-ui, sans-serif`;
      ctx.textBaseline = "top";
      // Contorno claro para que el texto se lea sobre cualquier fondo.
      ctx.save();
      ctx.lineWidth = Math.max(3, fs / 5);
      ctx.strokeStyle = "rgba(255,255,255,.95)";
      ctx.strokeText(f.texto, f.x, f.y);
      ctx.restore();
      ctx.fillText(f.texto, f.x, f.y);
    }
  });
}

const AnotadorCaptura = forwardRef(function AnotadorCaptura({ src, onCambio }, ref) {
  const canvasRef = useRef(null);
  const areaRef = useRef(null);
  const inputRef = useRef(null);
  const borradorRef = useRef(null);
  const textoRef = useRef(null);
  const colorRef = useRef(COLORES[0].key);

  const [img, setImg] = useState(null);
  const [tam, setTam] = useState({ w: 0, h: 0 });
  const [herramienta, setHerramienta] = useState("rect");
  const [color, setColor] = useState(COLORES[0].key);
  const [formas, setFormas] = useState([]);
  const [borrador, setBorrador] = useState(null);
  const [texto, setTextoState] = useState(null); // { x, y, valor } en coords de imagen

  const setTexto = (valor) => { textoRef.current = valor; setTextoState(valor); };
  const setBorradorAll = (valor) => { borradorRef.current = valor; setBorrador(valor); };
  colorRef.current = color;

  useEffect(() => {
    if (!src) { setImg(null); return undefined; }
    let vivo = true;
    const im = new Image();
    im.onload = () => { if (vivo) setImg(im); };
    im.src = src;
    return () => { vivo = false; };
  }, [src]);

  useEffect(() => { onCambio?.(formas.length); }, [formas.length, onCambio]);

  // Ajusta el canvas visible al area disponible conservando la proporcion.
  useLayoutEffect(() => {
    if (!img || !areaRef.current) return undefined;
    const calc = () => {
      const a = areaRef.current;
      if (!a) return;
      const f = Math.min(a.clientWidth / img.naturalWidth, a.clientHeight / img.naturalHeight, 1);
      setTam({ w: Math.floor(img.naturalWidth * f), h: Math.floor(img.naturalHeight * f) });
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(areaRef.current);
    return () => ro.disconnect();
  }, [img]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    dibujarFormas(ctx, borrador ? [...formas, borrador] : formas, img.naturalWidth);
  }, [img, formas, borrador]);

  const deshacer = useCallback(() => setFormas((f) => f.slice(0, -1)), []);
  const limpiar = useCallback(() => {
    setFormas([]);
    borradorRef.current = null;
    setBorrador(null);
    textoRef.current = null;
    setTextoState(null);
  }, []);

  // Ctrl+Z fuera de campos de texto.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.key.toLowerCase() !== "z") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      e.preventDefault();
      deshacer();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [deshacer]);

  useEffect(() => {
    if (!texto) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
    // Solo al abrir el cuadro de texto, no en cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto?.x, texto?.y]);

  const aCoordsImagen = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) * img.naturalWidth) / r.width,
      y: ((e.clientY - r.top) * img.naturalHeight) / r.height,
    };
  };

  const confirmarTexto = () => {
    const t = textoRef.current;
    if (!t) return;
    setTexto(null);
    const valor = t.valor.trim();
    if (valor) setFormas((f) => [...f, { tipo: "text", color: colorRef.current, x: t.x, y: t.y, texto: valor }]);
  };

  const onPointerDown = (e) => {
    if (!img || e.button !== 0) return;
    const p = aCoordsImagen(e);
    if (herramienta === "text") {
      if (texto) confirmarTexto();
      setTexto({ x: p.x, y: p.y, valor: "" });
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setBorradorAll(
      herramienta === "pen"
        ? { tipo: "pen", color, pts: [p] }
        : { tipo: herramienta, color, x1: p.x, y1: p.y, x2: p.x, y2: p.y }
    );
  };

  const onPointerMove = (e) => {
    const b = borradorRef.current;
    if (!b) return;
    const p = aCoordsImagen(e);
    setBorradorAll(b.tipo === "pen" ? { ...b, pts: [...b.pts, p] } : { ...b, x2: p.x, y2: p.y });
  };

  const onPointerUp = () => {
    const b = borradorRef.current;
    if (!b) return;
    setBorradorAll(null);
    const valida = b.tipo === "pen" ? b.pts.length > 1 : Math.hypot(b.x2 - b.x1, b.y2 - b.y1) > 6;
    if (valida) setFormas((f) => [...f, b]);
  };

  useImperativeHandle(ref, () => ({
    // Devuelve un PNG (Blob) con las anotaciones aplanadas sobre la captura.
    aplanar: () =>
      new Promise((resolve, reject) => {
        if (!img) { resolve(null); return; }
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        dibujarFormas(ctx, formas, img.naturalWidth);
        c.toBlob((b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen"))), "image/png");
      }),
  }), [img, formas]);

  const escalaVista = img && tam.w ? tam.w / img.naturalWidth : 1;
  const fsVista = img ? tamTexto(img.naturalWidth) * escalaVista : 14;

  return (
    <div className="sc-anot">
      <div className="sc-anot-toolbar">
        <div className="sc-anot-group">
          {HERRAMIENTAS.map((h) => (
            <button
              key={h.key}
              type="button"
              className={`sc-tool${herramienta === h.key ? " sc-tool--active" : ""}`}
              onClick={() => { if (texto) confirmarTexto(); setHerramienta(h.key); }}
              title={h.label}
              aria-label={h.label}
              disabled={!img}
            >
              <i className={h.icon} />
              <span>{h.label}</span>
            </button>
          ))}
        </div>
        <div className="sc-anot-group">
          {COLORES.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`sc-color${color === c.key ? " sc-color--active" : ""}`}
              style={{ background: c.key }}
              onClick={() => setColor(c.key)}
              title={c.label}
              aria-label={c.label}
              disabled={!img}
            />
          ))}
        </div>
        <div className="sc-anot-group sc-anot-group--end">
          <button type="button" className="sc-tool" onClick={deshacer} disabled={!formas.length} title="Deshacer (Ctrl+Z)">
            <i className="fa-solid fa-rotate-left" /><span>Deshacer</span>
          </button>
          <button type="button" className="sc-tool" onClick={limpiar} disabled={!formas.length && !texto} title="Limpiar anotaciones">
            <i className="fa-solid fa-trash-can" /><span>Limpiar</span>
          </button>
        </div>
      </div>

      <div className="sc-anot-area" ref={areaRef}>
        {!src && (
          <div className="sc-anot-vacio">
            <i className="fa-regular fa-image" />
            <div>No se pudo tomar la captura de pantalla.</div>
            <div className="sc-anot-vacio-sub">Puedes continuar y adjuntar imágenes en “Imágenes adicionales”.</div>
          </div>
        )}
        {src && img && (
          <div className="sc-anot-lienzo" style={{ width: tam.w, height: tam.h }}>
            <canvas
              ref={canvasRef}
              className={`sc-anot-canvas${herramienta === "text" ? " sc-anot-canvas--text" : ""}`}
              style={{ width: tam.w, height: tam.h }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
            {texto && (
              <input
                key={`${texto.x}-${texto.y}`}
                ref={inputRef}
                className="sc-anot-texto"
                value={texto.valor}
                maxLength={120}
                placeholder="Escribe y presiona Enter"
                style={{
                  left: texto.x * escalaVista,
                  top: texto.y * escalaVista,
                  color,
                  fontSize: fsVista,
                }}
                onChange={(e) => setTexto({ ...textoRef.current, valor: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); confirmarTexto(); }
                  if (e.key === "Escape") { e.stopPropagation(); setTexto(null); }
                }}
                onBlur={(e) => { if (e.target === inputRef.current) confirmarTexto(); }}
              />
            )}
          </div>
        )}
        {src && !img && <div className="sc-anot-vacio">Cargando captura…</div>}
      </div>
    </div>
  );
});

export default AnotadorCaptura;
